import { ChevronLeft, ChevronRight } from "lucide-react";
import { cx } from "../../utils";

/**
 * Page controls under a list: "21–40 of 132", page numbers and a page size.
 *
 * Reads the shape every admin list endpoint returns —
 * { total, page, pages, limit } — see backend _utils/paging.js.
 */

// 1 … 4 5 [6] 7 8 … 20 — first, last and two either side of the current page.
const pageList = (page, pages) => {
  const wanted = new Set([1, pages, page - 2, page - 1, page, page + 1, page + 2]);
  const sorted = [...wanted].filter((p) => p >= 1 && p <= pages).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push(`gap-${p}`);
    out.push(p);
  });
  return out;
};

const PageButton = ({ active, disabled, onClick, children, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    aria-current={active ? "page" : undefined}
    className={cx(
      "grid h-8 min-w-[2rem] place-items-center rounded-lg px-2 text-[13px] font-medium tabular-nums transition-colors",
      "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-500",
      active ? "bg-ink-900 text-white" : "text-ink-600 hover:bg-ink-100 disabled:opacity-40 disabled:hover:bg-transparent",
    )}
  >
    {children}
  </button>
);

const Pagination = ({ page, pages, total, limit, onPage, onLimit, sizes = [10, 20, 50, 100], noun = "items", compact, className }) => {
  if (!total) return null;

  const from = (page - 1) * limit + 1;
  const to = Math.min(total, page * limit);

  // For narrow columns: the range and previous/next only.
  if (compact) {
    return (
      <nav className={cx("flex items-center justify-between gap-2 border-t border-ink-150 px-3 py-2", className)} aria-label="Pagination">
        <p className="text-[12px] text-ink-500 tabular-nums">
          <span className="font-medium text-ink-800">
            {from}–{to}
          </span>{" "}
          of {total}
        </p>
        {pages > 1 && (
          <div className="flex items-center gap-0.5">
            <PageButton disabled={page <= 1} onClick={() => onPage(page - 1)} label="Previous page">
              <ChevronLeft size={15} />
            </PageButton>
            <span className="px-1.5 text-[12px] text-ink-500 tabular-nums">
              {page} / {pages}
            </span>
            <PageButton disabled={page >= pages} onClick={() => onPage(page + 1)} label="Next page">
              <ChevronRight size={15} />
            </PageButton>
          </div>
        )}
      </nav>
    );
  }

  return (
    <nav
      className={cx("flex flex-wrap items-center justify-between gap-3 border-t border-ink-150 px-5 py-3", className)}
      aria-label="Pagination"
    >
      <p className="text-[12.5px] text-ink-500 tabular-nums">
        <span className="font-medium text-ink-800">
          {from}–{to}
        </span>{" "}
        of <span className="font-medium text-ink-800">{total}</span> {noun}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        {onLimit && (
          <label className="flex items-center gap-2 text-[12.5px] text-ink-500">
            Rows
            <select
              value={limit}
              onChange={(e) => onLimit(Number(e.target.value))}
              className="h-8 cursor-pointer rounded-lg border border-ink-200 bg-white px-2 text-[13px] text-ink-800 shadow-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            >
              {[...new Set([...sizes, limit])].sort((a, b) => a - b).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}

        {pages > 1 && (
          <div className="flex items-center gap-0.5">
            <PageButton disabled={page <= 1} onClick={() => onPage(page - 1)} label="Previous page">
              <ChevronLeft size={15} />
            </PageButton>
            {pageList(page, pages).map((p) =>
              typeof p === "string" ? (
                <span key={p} className="px-1 text-[13px] text-ink-400" aria-hidden>
                  …
                </span>
              ) : (
                <PageButton key={p} active={p === page} onClick={() => onPage(p)} label={`Page ${p}`}>
                  {p}
                </PageButton>
              ),
            )}
            <PageButton disabled={page >= pages} onClick={() => onPage(page + 1)} label="Next page">
              <ChevronRight size={15} />
            </PageButton>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Pagination;
